"use client";

import Link from "next/link";
import { CheckCircle2 } from "lucide-react";
import { useAppFeatureFlag } from "@/components/feature-flags/feature-flags-provider";
import {
  SITE_CONTROL_NAME,
  SITE_CONTROL_TAGLINE,
  SITE_CONTROL_PRICE_HEADLINE,
  SITE_CONTROL_FEATURES,
  SITE_CONTROL_CTA,
} from "@/lib/site-control-copy";

/**
 * Site Control — reusable marketing callout band for the per-site enterprise
 * tier (principal contractors running live sites).
 *
 * FLAG-GATED (self-gating): renders nothing unless the DB-backed
 * `site_control` flag is on, read from the nearest AppFeatureFlagsProvider
 * (added site-wide in app/(marketing)/layout.tsx). So a flag toggle removes
 * every Site Control mention across the site in lockstep with the nav/footer
 * links and the /site-control + /principal-contractors landing pages (which
 * 404 when the flag is off). Drop it into any marketing page under the
 * marketing layout — no per-page flag plumbing required.
 *
 * Copy is sourced from lib/site-control-copy.ts so the strings stay
 * consistent with the pricing card, the landing page and the compare pages.
 */
export function SiteControlCallout({ className }: { className?: string }) {
  const enabled = useAppFeatureFlag("site_control");
  if (!enabled) return null;

  // Lead with the most concrete on-site capabilities; the full list lives on
  // the pricing card + landing page.
  const highlights = SITE_CONTROL_FEATURES.slice(0, 6);

  return (
    <section
      data-testid="site-control-callout"
      aria-labelledby="site-control-callout-heading"
      className={className ?? "mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16"}
    >
      <div className="overflow-hidden rounded-2xl border border-navy/20 bg-navy text-white shadow-lg">
        <div className="grid gap-8 p-8 sm:p-10 lg:grid-cols-2 lg:items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white">
              Enterprise &middot; per active site
            </span>
            <h2
              id="site-control-callout-heading"
              className="mt-4 text-2xl font-extrabold tracking-tight sm:text-3xl"
            >
              {SITE_CONTROL_NAME}
            </h2>
            <p className="mt-3 text-base leading-relaxed text-white/80">
              {SITE_CONTROL_TAGLINE}
            </p>
            <p className="mt-4 text-sm font-semibold text-white">
              {SITE_CONTROL_PRICE_HEADLINE}
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                href="/site-control"
                className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-accent px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-dark"
              >
                {SITE_CONTROL_CTA}
              </Link>
              <Link
                href="/pricing"
                className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-white/30 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                Compare all plans
              </Link>
            </div>
          </div>
          <ul className="space-y-3">
            {highlights.map((feature) => (
              <li
                key={feature}
                className="flex items-start gap-2 text-sm text-white/90"
              >
                <CheckCircle2
                  className="mt-0.5 h-4 w-4 shrink-0 text-accent"
                  aria-hidden="true"
                />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
